<template>
  <div
    id="app"
    class="app-container"
    :class="{
      'dyslexia-mode': isDyslexiaMode,
      'autism-mode': isAutismMode,
      'reduced-motion': isReducedMotion,
      'high-contrast': isHighContrast,
      'predictable-layout': isPredictableLayout,
    }"
  >
    <div class="content-wrapper">
      <router-view></router-view>
    </div>

    <div
      v-if="!isDashboardPage && !isLoginPage && !isAccompagnateurPath && !isLandingPage"
      class="dashboard-button"
      @click="goToDashboard"
      aria-label="Retour au tableau de bord"
      role="button"
      tabindex="0"
      @keydown.enter="goToDashboard"
    >
      <i class="mdi mdi-home dashboard-icon"></i>
      <span class="tooltip">Tableau de bord</span>
    </div>

    <!-- Bouton Plein Écran -->
    <div
      v-if="!isLandingPage && !isAccompagnateurPath"
      class="fullscreen-button"
      @click="toggleFullScreen"
      :class="{ active: isFullScreen }"
      aria-label="Mode plein écran"
      role="button"
      tabindex="0"
      @keydown.enter="toggleFullScreen"
    >
      <i
        class="mdi"
        :class="isFullScreen ? 'mdi-fullscreen-exit' : 'mdi-fullscreen'"
      ></i>
      <span class="tooltip">{{ isFullScreen ? 'Quitter le plein écran' : 'Plein écran' }}</span>
    </div>

    <!-- Boutons de navigation haut/bas -->
    <div
      v-if="!isAccompagnateurPath"
      class="scroll-navigation-buttons"
    >
      <div
        class="scroll-button scroll-up"
        @click="scrollToTop"
        aria-label="Défiler vers le haut"
        role="button"
        tabindex="0"
        @keydown.enter="scrollToTop"
      >
        <i class="mdi mdi-chevron-up"></i>
      </div>
      <div
        class="scroll-button scroll-down"
        @click="scrollToBottom"
        aria-label="Défiler vers le bas"
        role="button"
        tabindex="0"
        @keydown.enter="scrollToBottom"
      >
        <i class="mdi mdi-chevron-down"></i>
      </div>
    </div>

    <!-- Widget Button with Icon -->
    <div
      v-if="!isAccompagnateurPath"
      class="accessibility-widget"
      @click="toggleWidget"
      :class="{ active: showWidget }"
      aria-label="Options d'accessibilité"
      role="button"
      tabindex="0"
      @keydown.enter="toggleWidget"
    >
      <i class="mdi mdi-account-cog inclusive-icon"></i>
      <span class="tooltip">Accessibilité</span>
    </div>

    <!-- Options Panel with Categories -->
    <div v-if="showWidget && !isAccompagnateurPath" class="accessibility-panel">
      <div class="panel-header">
        <h2>Options d'accessibilité</h2>
        <button @click="toggleWidget" class="close-button" aria-label="Fermer">
          <i class="mdi mdi-close"></i>
        </button>
      </div>

      <div class="panel-tabs">
        <button
          @click="activeTab = 'general'"
          :class="{ 'active-tab': activeTab === 'general' }"
        >
          <i class="mdi mdi-cog-outline"></i> Général
        </button>
        <button
          @click="activeTab = 'reading'"
          :class="{ 'active-tab': activeTab === 'reading' }"
        >
          <i class="mdi mdi-book-open-variant"></i> Lecture
        </button>
        <button
          @click="activeTab = 'visual'"
          :class="{ 'active-tab': activeTab === 'visual' }"
        >
          <i class="mdi mdi-eye-outline"></i> Visuel
        </button>
      </div>

      <!-- General Tab -->
      <div v-show="activeTab === 'general'" class="panel-content">
        <div class="option-item">
          <button
            @click="toggleTextToSpeech"
            :class="{ 'active-option': isTextToSpeech }"
          >
            <i class="mdi mdi-volume-high option-icon"></i>
            <span class="option-text">Lire la page</span>
          </button>
        </div>
        <div class="option-item">
          <button
            @click="toggleReadingMask"
            :class="{ 'active-option': showReadingMask }"
          >
            <i class="mdi mdi-eye-off-outline option-icon"></i>
            <span class="option-text">Masque de lecture</span>
          </button>
        </div>
        <div class="option-item">
          <button
            @click="makeCursorLarger"
            :class="{ 'active-option': isLargeCursor }"
          >
            <i class="mdi mdi-cursor-default-outline option-icon"></i>
            <span class="option-text">Agrandir le curseur</span>
          </button>
        </div>
      </div>

      <!-- Reading Tab -->
      <div v-show="activeTab === 'reading'" class="panel-content">
        <div class="option-item">
          <button
            @click="toggleDyslexiaMode"
            :class="{ 'active-option': isDyslexiaMode }"
          >
            <i class="mdi mdi-book-open-page-variant-outline option-icon"></i>
            <span class="option-text">Mode dyslexie</span>
          </button>
        </div>
        <div class="option-item">
          <button
            @click="toggleTextSize"
            :class="{ 'active-option': isLargeText }"
          >
            <i class="mdi mdi-format-size option-icon"></i>
            <span class="option-text">Agrandir le texte</span>
          </button>
        </div>
        <div class="option-item">
          <button
            @click="toggleTextSpacing"
            :class="{ 'active-option': isSpacedText }"
          >
            <i class="mdi mdi-format-letter-spacing-variant option-icon"></i>
            <span class="option-text">Espacer le texte</span>
          </button>
        </div>
        <div class="option-item">
          <button
            @click="toggleReadingGuide"
            :class="{ 'active-option': showReadingGuide }"
          >
            <i class="mdi mdi-ruler option-icon"></i>
            <span class="option-text">Guide de lecture</span>
          </button>
        </div>
      </div>

      <!-- Visual Tab -->
      <div v-show="activeTab === 'visual'" class="panel-content">
        <div class="option-item">
          <button
            @click="toggleHighlightClickable"
            :class="{ 'active-option': isHighlightClickable }"
          >
            <i class="mdi mdi-cursor-pointer option-icon"></i>
            <span class="option-text">Surligner les éléments cliquables</span>
          </button>
        </div>
        <div class="option-item">
          <label class="option-label">Alignement du texte:</label>
          <div class="button-group">
            <button
              @click="alignText('left')"
              class="small-button"
              :class="{ 'active-option': textAlignment === 'left' }"
            >
              <i class="mdi mdi-format-align-left"></i>
            </button>
            <button
              @click="alignText('center')"
              class="small-button"
              :class="{ 'active-option': textAlignment === 'center' }"
            >
              <i class="mdi mdi-format-align-center"></i>
            </button>
            <button
              @click="alignText('right')"
              class="small-button"
              :class="{ 'active-option': textAlignment === 'right' }"
            >
              <i class="mdi mdi-format-align-right"></i>
            </button>
          </div>
        </div>
        <div class="option-item">
          <button
            @click="toggleColorTheme"
            :class="{ 'active-option': colorTheme !== 'default' }"
          >
            <i class="mdi mdi-palette-outline option-icon"></i>
            <span class="option-text">
              Thème: {{ colorThemeLabel }}
            </span>
          </button>
        </div>
      </div>

      <div class="panel-footer">
        <button @click="resetAllSettings" class="reset-button">
          <i class="mdi mdi-refresh"></i> Réinitialiser
        </button>
      </div>
    </div>

    <!-- Reading Mask -->
    <template v-if="showReadingMask">
      <div class="reading-mask-overlay mask-top" :style="maskTopStyles"></div>
      <div class="reading-mask-overlay mask-bottom" :style="maskBottomStyles"></div>
      <div class="reading-mask-overlay mask-left" :style="maskLeftStyles"></div>
      <div class="reading-mask-overlay mask-right" :style="maskRightStyles"></div>
    </template>

    <!-- Reading Guide -->
    <div v-if="showReadingGuide" class="reading-guide" :style="readingGuideStyles"></div>
  </div>
</template>

<script>
export default {
  name: 'App',
  data() {
    return {
      isFullScreen: false,

      // Panel state
      showWidget: false,
      activeTab: 'general',

      // General accessibility
      isTextToSpeech: false,
      isVoiceNavigation: false,
      isLargeCursor: false,

      // Autism specific modes
      isAutismMode: false,
      isReducedMotion: false,
      isHighContrast: false,
      isPredictableLayout: false,
      showQuitZone: false,

      // Reading modes
      isDyslexiaMode: false,
      isLargeText: false,
      isSpacedText: false,
      showReadingMask: false,
      showReadingGuide: false,

      // Visual preferences
      isHighlightClickable: false,
      textAlignment: 'center',
      colorTheme: 'default',

      // Reading Mask and Guide styles
      maskTopStyles: {},
      maskBottomStyles: {},
      maskLeftStyles: {},
      maskRightStyles: {},
      readingGuideStyles: {},

      // Speech synthesis
      speechSynthesis: null,
      speechUtterance: null,

      // Audio context for feedback sounds
      audioContext: null,

      // Audio feedback preferences
      audioFeedbackEnabled: false,

      isDashboardPage: false,
      isLoginPage: false,
      isAccompagnateurPath: false,
      isLandingPage: false,

      // Observer for DOM changes
      observer: null,
    }
  },

  computed: {
    colorThemeLabel() {
      const themes = {
        default: 'Standard',
        warm: 'Chaleureux',
        cool: 'Apaisant',
        dark: 'Sombre',
        monochrome: 'Monochrome',
      }
      return themes[this.colorTheme]
    },
  },

  mounted() {
    // Load user preferences from localStorage if available
    this.loadUserPreferences()

    // Initialize speech synthesis
    if ('speechSynthesis' in window) {
      this.speechSynthesis = window.speechSynthesis
    }

    this.checkIfDashboardPage()
    document.addEventListener('fullscreenchange', this.fullScreenChangeHandler)
    document.addEventListener(
      'webkitfullscreenchange',
      this.fullScreenChangeHandler,
    )
    document.addEventListener(
      'mozfullscreenchange',
      this.fullScreenChangeHandler,
    )
    document.addEventListener(
      'MSFullscreenChange',
      this.fullScreenChangeHandler,
    )
  },

  beforeUnmount() {
    // Nettoyage des écouteurs d'événements lors de la destruction du composant
    document.removeEventListener(
      'fullscreenchange',
      this.fullScreenChangeHandler,
    )
    document.removeEventListener(
      'webkitfullscreenchange',
      this.fullScreenChangeHandler,
    )
    document.removeEventListener(
      'mozfullscreenchange',
      this.fullScreenChangeHandler,
    )
    document.removeEventListener(
      'MSFullscreenChange',
      this.fullScreenChangeHandler,
    )

    if (this.observer) {
      this.observer.disconnect()
      this.observer = null
    }
  },

  watch: {
    $route() {
      this.checkIfDashboardPage()

      this.$nextTick(() => {
        this.checkIfDashboardPage()
      })

      if (this.isHighlightClickable) {
        // Use setTimeout to ensure DOM is fully updated after route transition
        setTimeout(() => {
          this.highlightClickableElements()
        }, 300)
      }

      // Apply predictable layout for autism mode when route changes
      if (this.isPredictableLayout) {
        this.$nextTick(() => {
          this.applyPredictableLayout()
        })
      }
    },
  },

  methods: {
    toggleFullScreen() {
      if (!this.isFullScreen) {
        this.enterFullScreen()
      } else {
        this.exitFullScreen()
      }

      // Son de retour si activé
      if (this.audioFeedbackEnabled) {
        if (this.isFullScreen) {
          this.playAudioFeedback('toggle-on')
        } else {
          this.playAudioFeedback('toggle-off')
        }
      }
    },

    // Activer le mode plein écran
    enterFullScreen() {
      const docEl = document.documentElement

      const requestFullScreen =
        docEl.requestFullscreen ||
        docEl.mozRequestFullScreen ||
        docEl.webkitRequestFullscreen ||
        docEl.msRequestFullscreen

      if (requestFullScreen) {
        requestFullScreen.call(docEl)
      }
    },

    // Quitter le mode plein écran
    exitFullScreen() {
      // Vérifier d'abord si le document est en mode plein écran
      const fullscreenElement =
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement

      // Ne tenter de quitter le plein écran que si nous sommes en plein écran
      if (fullscreenElement) {
        const exitFullScreen =
          document.exitFullscreen ||
          document.mozCancelFullScreen ||
          document.webkitExitFullscreen ||
          document.msExitFullscreen

        if (exitFullScreen) {
          try {
            exitFullScreen.call(document)
          } catch (error) {
            console.error(
              'Erreur lors de la sortie du mode plein écran:',
              error,
            )
            // Forcer la mise à jour de l'état
            this.isFullScreen = false
          }
        }
      } else {
        // Si nous ne sommes pas en plein écran, mettre à jour l'état
        this.isFullScreen = false
      }
    },

    // Gestionnaire de l'événement de changement d'état de plein écran
    fullScreenChangeHandler() {
      const fullscreenElement =
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement

      // Update the state variable
      this.isFullScreen = !!fullscreenElement

      // Enregistrer la préférence utilisateur uniquement si nous sommes en plein écran
      // This prevents saving "false" when exiting fullscreen due to a page reload
      if (this.isFullScreen) {
        this.saveUserPreferences()
      } else {
        // When exiting fullscreen mode, update localStorage with the current state
        const savedPrefs = localStorage.getItem('accessibilityPreferences')
        if (savedPrefs) {
          const prefs = JSON.parse(savedPrefs)
          prefs.isFullScreen = false
          localStorage.setItem(
            'accessibilityPreferences',
            JSON.stringify(prefs),
          )
        }
      }
    },

    checkIfDashboardPage() {
      const route = this.$route

      const isDashboard =
        route.name === 'Dashboard' ||
        route.name === 'ImmersiveDashboard' ||
        route.path === '/jeune/dashboard' ||
        route.path === '/jeune/onboarding' ||
        route.path === '/'

      const isLogin =
        route.name === 'Login' ||
        route.name === 'LoginPage' ||
        route.path === '/login' ||
        route.path === '/se-connecter'

      // Vérifier si on est dans le parcours accompagnateur
      const isAccompagnateur =
        route.name === 'AccompagnateurOnboarding' ||
        route.name === 'DashboardAccompagnateur' ||
        route.path.includes('/accompagnant') ||
        route.path === '/onboarding-accompagnateur'

      // Vérifier si on est sur la page d'accueil (landing page)
      const isLanding =
        route.name === 'LandingPage' || 
        route.path === '/' ||
        route.path === '/commencer'

      this.isDashboardPage = isDashboard
      this.isLoginPage = isLogin
      this.isAccompagnateurPath = isAccompagnateur
      this.isLandingPage = isLanding
    },

    goToDashboard() {
      this.$router.push('/jeune/dashboard')

      if (this.audioFeedbackEnabled) {
        this.playAudioFeedback('navigation')
      }
    },

    scrollToTop() {
      // Trouver l'élément avec scroll actif (popup, modal, panel, etc.)
      const scrollableElement = this.findActiveScrollableElement()

      if (scrollableElement && scrollableElement !== document.documentElement) {
        // Scroll progressif vers le haut dans l'élément scrollable
        const currentScroll = scrollableElement.scrollTop
        const scrollAmount = scrollableElement.clientHeight * 0.8 // 80% de la hauteur visible
        scrollableElement.scrollTo({
          top: Math.max(0, currentScroll - scrollAmount),
          behavior: 'smooth'
        })
      } else {
        // Scroll progressif vers le haut dans la fenêtre principale
        const currentScroll = window.pageYOffset || document.documentElement.scrollTop
        const scrollAmount = window.innerHeight * 0.8
        window.scrollTo({
          top: Math.max(0, currentScroll - scrollAmount),
          behavior: 'smooth'
        })
      }

      if (this.audioFeedbackEnabled) {
        this.playAudioFeedback('navigation')
      }
    },

    scrollToBottom() {
      // Trouver l'élément avec scroll actif (popup, modal, panel, etc.)
      const scrollableElement = this.findActiveScrollableElement()

      if (scrollableElement && scrollableElement !== document.documentElement) {
        // Scroll progressif vers le bas dans l'élément scrollable
        const currentScroll = scrollableElement.scrollTop
        const scrollAmount = scrollableElement.clientHeight * 0.8 // 80% de la hauteur visible
        const maxScroll = scrollableElement.scrollHeight - scrollableElement.clientHeight
        scrollableElement.scrollTo({
          top: Math.min(maxScroll, currentScroll + scrollAmount),
          behavior: 'smooth'
        })
      } else {
        // Scroll progressif vers le bas dans la fenêtre principale
        const currentScroll = window.pageYOffset || document.documentElement.scrollTop
        const scrollAmount = window.innerHeight * 0.8
        const maxScroll = document.documentElement.scrollHeight - window.innerHeight
        window.scrollTo({
          top: Math.min(maxScroll, currentScroll + scrollAmount),
          behavior: 'smooth'
        })
      }

      if (this.audioFeedbackEnabled) {
        this.playAudioFeedback('navigation')
      }
    },

    findActiveScrollableElement() {
      // Liste des sélecteurs d'éléments scrollables communs
      // Ordre de priorité : du plus spécifique au plus général
      const scrollableSelectors = [
        '.rewards-container', // RewardsComponent
        '.accessibility-panel',
        '.badge-modal',
        '.logout-modal',
        '.onboarding-container',
        '.content-container',
        '.step-container',
        '.calibration-container',
        '.typing-target',
        '.results-modal',
        '.flamou-choice-container',
        '.game-container',
        '.speed-game-container',
        '.environment-container',
        '.scratch-game-container',
        '.job-discovery-container',
        '[role="dialog"]',
        '.modal',
        '.popup',
        '.overlay',
        '.panel',
        '.scrollable'
      ]

      // Chercher les éléments scrollables visibles
      for (const selector of scrollableSelectors) {
        const elements = document.querySelectorAll(selector)
        for (const element of elements) {
          // Vérifier si l'élément est visible et a un défilement
          const style = window.getComputedStyle(element)
          const isVisible = style.display !== 'none' && style.visibility !== 'hidden' && element.offsetParent !== null
          const hasOverflow = element.scrollHeight > element.clientHeight
          const isScrollable = ['auto', 'scroll'].includes(style.overflowY) || ['auto', 'scroll'].includes(style.overflow)

          if (isVisible && hasOverflow && isScrollable) {
            console.log('Élément scrollable détecté:', selector, element)
            return element
          }
        }
      }

      // Si aucun élément scrollable trouvé, retourner document.documentElement
      return document.documentElement
    },

    toggleWidget() {
      const wasOpen = this.showWidget
      this.showWidget = !this.showWidget

      // Play a soft sound for feedback if audio is enabled
      if (this.audioFeedbackEnabled) {
        if (this.showWidget) {
          this.playAudioFeedback('open')
        } else if (wasOpen) {
          this.playAudioFeedback('close')
        }
      }
    },

    // ===== General Settings =====
    toggleTextToSpeech() {
      this.isTextToSpeech = !this.isTextToSpeech

      if (this.isTextToSpeech) {
        this.readPageContent()
      } else if (this.speechSynthesis) {
        this.speechSynthesis.cancel()
      }

      this.saveUserPreferences()
    },

    readPageContent() {
      if (!this.speechSynthesis) return

      // Get main content excluding navigation, widgets and buttons
      const contentWrapper = document.querySelector('.content-wrapper')
      if (!contentWrapper) return

      // Clone to not modify original DOM
      const clone = contentWrapper.cloneNode(true)
      // Remove elements we don't want to read
      clone.querySelectorAll('button, nav, .accessibility-widget, .tooltip, script, style').forEach(el => el.remove())

      const textToRead = clone.textContent.replace(/\s+/g, ' ').trim()
      if (!textToRead) return

      // Create utterance with user-friendly settings
      this.speechUtterance = new SpeechSynthesisUtterance(textToRead)
      this.speechUtterance.rate = 0.9 // Slightly slower rate for better comprehension
      this.speechUtterance.pitch = 1

      // Use a French voice if available
      const voices = this.speechSynthesis.getVoices()
      const frenchVoice = voices.find((voice) => voice.lang.includes('fr'))
      if (frenchVoice) {
        this.speechUtterance.voice = frenchVoice
      }

      this.speechSynthesis.speak(this.speechUtterance)
    },

    toggleVoiceNavigation() {
      // Feature not implemented yet, but we keep the toggle for future use
      this.isVoiceNavigation = !this.isVoiceNavigation
      this.saveUserPreferences()
    },

    initializeVoiceNavigation() {
      // Placeholder for actual implementation
    },

    disableVoiceNavigation() {
      // Placeholder for actual implementation
    },

    makeCursorLarger() {
      this.isLargeCursor = !this.isLargeCursor

      if (this.isLargeCursor) {
        document.body.classList.add('large-cursor')
      } else {
        document.body.classList.remove('large-cursor')
      }

      this.saveUserPreferences()
    },

    // ===== Autism Specific Methods =====
    toggleAutismMode() {
      this.isAutismMode = !this.isAutismMode

      if (this.isAutismMode) {
        // Apply autism-friendly settings
        this.isReducedMotion = true
        this.isPredictableLayout = true
        document.body.classList.add('autism-friendly')
        this.applyAutismFriendlyStyles()

        // Play audio feedback
        if (this.audioFeedbackEnabled) {
          this.playAudioFeedback('toggle-on')
        }
      } else {
        document.body.classList.remove('autism-friendly')

        // Play audio feedback
        if (this.audioFeedbackEnabled) {
          this.playAudioFeedback('toggle-off')
        }
      }

      this.saveUserPreferences()
    },

    applyAutismFriendlyStyles() {
      // Simplify layout
      this.reduceAnimations()
      this.applyPredictableLayout()

      // Apply clearer font and spacing for better readability
      document.body.style.setProperty(
        '--autism-font',
        '"Open Sans", "Arial", sans-serif',
      )
      document.body.style.setProperty('--autism-line-height', '1.5')
      document.body.style.setProperty('--autism-paragraph-spacing', '1.2em')
    },

    toggleReducedMotion() {
      this.isReducedMotion = !this.isReducedMotion
      this.reduceAnimations()
      this.saveUserPreferences()
    },

    reduceAnimations() {
      if (this.isReducedMotion) {
        document.body.classList.add('reduced-motion')
      } else {
        document.body.classList.remove('reduced-motion')
      }
    },

    togglePredictableLayout() {
      this.isPredictableLayout = !this.isPredictableLayout
      this.applyPredictableLayout()
      this.saveUserPreferences()
    },

    applyPredictableLayout() {
      if (this.isPredictableLayout) {
        document.body.classList.add('predictable-layout')

        // Apply consistent spacing and structure
        const sections = document.querySelectorAll(
          'section, article, div.content-section',
        )
        sections.forEach((section) => {
          section.style.margin = '1.5rem 0'
          section.style.padding = '1rem'
          section.style.borderRadius = '8px'
        })
      } else {
        document.body.classList.remove('predictable-layout')
      }
    },

    toggleHighContrast() {
      this.isHighContrast = !this.isHighContrast

      if (this.isHighContrast) {
        document.body.classList.add('high-contrast')
      } else {
        document.body.classList.remove('high-contrast')
      }

      this.saveUserPreferences()
    },

    toggleQuitZone() {
      this.showQuitZone = !this.showQuitZone

      if (this.showQuitZone) {
        // Pause any media and animations when the quit zone is active
        document.querySelectorAll('video, audio').forEach((media) => {
          media.pause()
        })

        // Stop any running speech synthesis
        if (this.speechSynthesis && this.speechSynthesis.speaking) {
          this.speechSynthesis.cancel()
        }

        // Play a very gentle calming sound
        if (this.audioFeedbackEnabled) {
          this.playAudioFeedback('quit-zone')
        }
      }
    },

    playAudioFeedback(action) {
      // Create AudioContext only when needed (to avoid autoplay restrictions)
      if (!this.audioContext) {
        this.audioContext = new (window.AudioContext ||
          window.webkitAudioContext)()
      }

      // Different sound profiles based on action type
      let frequency, duration, type, volume

      switch (action) {
        case 'open':
          // Gentle rising tone for opening panel
          frequency = 440 // A4 note (Hz)
          duration = 0.3 // seconds
          type = 'sine' // smooth waveform
          volume = 0.1 // quiet (max is 1.0)
          this.playTone(frequency, frequency + 200, duration, type, volume)
          break

        case 'close':
          // Gentle falling tone for closing panel
          frequency = 440
          duration = 0.3
          type = 'sine'
          volume = 0.1
          this.playTone(frequency + 100, frequency, duration, type, volume)
          break

        case 'toggle-on':
          // Short pleasant tone for enabling a feature
          frequency = 523.25 // C5 note (Hz)
          duration = 0.15
          type = 'sine'
          volume = 0.1
          this.playTone(frequency, frequency, duration, type, volume)
          break

        case 'toggle-off':
          // Very soft tone for disabling a feature
          frequency = 392 // G4 note (Hz)
          duration = 0.15
          type = 'sine'
          volume = 0.08
          this.playTone(frequency, frequency, duration, type, volume)
          break

        case 'error':
          // Gentle low tone for errors (non-jarring)
          frequency = 330 // E4 note (Hz)
          duration = 0.2
          type = 'sine'
          volume = 0.1
          this.playTone(frequency, frequency - 30, duration, type, volume)
          break

        case 'quit-zone':
          // Very soft, calming tone for entering quiet zone
          frequency = 294 // D4 note (Hz)
          duration = 0.5
          type = 'sine'
          volume = 0.05
          this.playTone(frequency, frequency, duration, type, volume)
          break

        default:
          // Default gentle feedback
          frequency = 440
          duration = 0.2
          type = 'sine'
          volume = 0.08
          this.playTone(frequency, frequency, duration, type, volume)
      }
    },

    playTone(startFreq, endFreq, duration, type = 'sine', volume = 0.1) {
      // Safety check - make sure audioContext exists
      if (!this.audioContext) return

      try {
        // Create oscillator and gain nodes
        const oscillator = this.audioContext.createOscillator()
        const gainNode = this.audioContext.createGain()

        // Set initial values
        oscillator.type = type
        oscillator.frequency.setValueAtTime(
          startFreq,
          this.audioContext.currentTime,
        )

        // If start and end frequencies are different, create a frequency ramp
        if (startFreq !== endFreq) {
          oscillator.frequency.linearRampToValueAtTime(
            endFreq,
            this.audioContext.currentTime + duration,
          )
        }

        // Set volume with fade in/out to avoid clicks
        gainNode.gain.setValueAtTime(0, this.audioContext.currentTime)
        gainNode.gain.linearRampToValueAtTime(
          volume,
          this.audioContext.currentTime + 0.05,
        )
        gainNode.gain.linearRampToValueAtTime(
          0,
          this.audioContext.currentTime + duration,
        )

        // Connect nodes
        oscillator.connect(gainNode)
        gainNode.connect(this.audioContext.destination)

        // Play and automatically stop
        oscillator.start()
        oscillator.stop(this.audioContext.currentTime + duration)

        // Cleanup when done
        oscillator.onended = () => {
          oscillator.disconnect()
          gainNode.disconnect()
        }
      } catch (error) {
        console.error('Error playing audio feedback:', error)
      }
    },

    // ===== Reading Settings =====
    toggleDyslexiaMode() {
      this.isDyslexiaMode = !this.isDyslexiaMode

      if (this.isDyslexiaMode) {
        document.body.classList.add('dyslexia-mode')
      } else {
        document.body.classList.remove('dyslexia-mode')
      }

      this.saveUserPreferences()
    },

    toggleTextSize() {
      this.isLargeText = !this.isLargeText

      if (this.isLargeText) {
        document.documentElement.classList.add('large-text')
      } else {
        document.documentElement.classList.remove('large-text')
      }

      this.saveUserPreferences()
    },

    toggleTextSpacing() {
      this.isSpacedText = !this.isSpacedText

      if (this.isSpacedText) {
        document.body.classList.add('spaced-text')
      } else {
        document.body.classList.remove('spaced-text')
      }

      this.saveUserPreferences()
    },

    toggleReadingMask() {
      this.showReadingMask = !this.showReadingMask

      if (this.showReadingMask) {
        document.addEventListener('mousemove', this.updateMaskPosition)
        // Initial call to set position
        this.updateMaskPosition({ clientY: window.innerHeight / 2 })
      } else {
        document.removeEventListener('mousemove', this.updateMaskPosition)
      }

      this.saveUserPreferences()
    },

    updateMaskPosition(event) {
      const maskHeight = 150
      const maskTopPosition = event.clientY - maskHeight / 2

      this.maskTopStyles = {
        height: `${maskTopPosition}px`,
      }
      this.maskBottomStyles = {
        top: `${maskTopPosition + maskHeight}px`,
        height: `${window.innerHeight - (maskTopPosition + maskHeight)}px`,
      }

      this.maskLeftStyles = {
        height: `${maskHeight}px`,
        top: `${maskTopPosition}px`,
      }
      this.maskRightStyles = {
        height: `${maskHeight}px`,
        top: `${maskTopPosition}px`,
      }
    },

    toggleReadingGuide() {
      this.showReadingGuide = !this.showReadingGuide

      if (this.showReadingGuide) {
        document.addEventListener('mousemove', this.updateGuidePosition)
        // Initial call to set position
        this.updateGuidePosition({
          clientX: window.innerWidth / 2,
          clientY: window.innerHeight / 2,
        })
      } else {
        document.removeEventListener('mousemove', this.updateGuidePosition)
      }

      this.saveUserPreferences()
    },

    updateGuidePosition(event) {
      const guideHeight = 30
      const guideWidth = window.innerWidth * 0.5
      const guideTop = event.clientY - guideHeight / 2
      const guideLeft = event.clientX - guideWidth / 2

      this.readingGuideStyles = {
        top: `${guideTop}px`,
        left: `${guideLeft}px`,
        width: `${guideWidth}px`,
      }
    },

    // ===== Visual Settings =====
    toggleHighlightClickable() {
      this.isHighlightClickable = !this.isHighlightClickable
      this.highlightClickableElements()

      if (this.isHighlightClickable) {
        this.setupObserver()
      } else {
        if (this.observer) {
          this.observer.disconnect()
          this.observer = null
        }
      }

      this.saveUserPreferences()
    },

    highlightClickableElements() {
      const clickableElements = document.querySelectorAll(
        'a, button, .small-button, [role="button"]',
      )
      clickableElements.forEach((element) => {
        if (this.isHighlightClickable) {
          element.classList.add('highlight-clickable')
        } else {
          element.classList.remove('highlight-clickable')
        }
      })
    },

    setupObserver() {
      if (this.observer) return

      this.observer = new MutationObserver((mutations) => {
        let shouldUpdate = false
        for (const mutation of mutations) {
          if (mutation.addedNodes.length > 0) {
            shouldUpdate = true
            break
          }
        }

        if (shouldUpdate && this.isHighlightClickable) {
          this.highlightClickableElements()
        }
      })

      this.observer.observe(document.body, {
        childList: true,
        subtree: true,
      })
    },

    alignText(alignment) {
      this.textAlignment = alignment

      document.body.classList.remove(
        'align-left',
        'align-center',
        'align-right',
      )
      document.body.classList.add(`align-${alignment}`)

      this.saveUserPreferences()
    },

    toggleColorTheme() {
      const themes = ['default', 'warm', 'cool', 'dark', 'monochrome']
      const currentIndex = themes.indexOf(this.colorTheme)
      const nextIndex = (currentIndex + 1) % themes.length
      this.colorTheme = themes[nextIndex]

      document.body.className = document.body.className.replace(
        /theme-\w+/g,
        '',
      )
      document.body.classList.add(`theme-${this.colorTheme}`)

      this.saveUserPreferences()
    },

    toggleAudioFeedback() {
      this.audioFeedbackEnabled = !this.audioFeedbackEnabled

      // Play feedback only if turning it ON (not when turning it off)
      if (this.audioFeedbackEnabled) {
        this.playAudioFeedback('toggle-on')
      }

      this.saveUserPreferences()
    },

    // ===== User Preferences =====
    saveUserPreferences() {
      // Save all user preferences to localStorage
      const preferences = {
        isAutismMode: this.isAutismMode,
        isReducedMotion: this.isReducedMotion,
        isPredictableLayout: this.isPredictableLayout,
        isHighContrast: this.isHighContrast,
        isDyslexiaMode: this.isDyslexiaMode,
        isLargeText: this.isLargeText,
        isSpacedText: this.isSpacedText,
        isLargeCursor: this.isLargeCursor,
        isHighlightClickable: this.isHighlightClickable,
        textAlignment: this.textAlignment,
        colorTheme: this.colorTheme,
        audioFeedbackEnabled: this.audioFeedbackEnabled,
        isFullScreen: this.isFullScreen,
      }

      localStorage.setItem(
        'accessibilityPreferences',
        JSON.stringify(preferences),
      )
    },

    loadUserPreferences() {
      const savedPreferences = localStorage.getItem('accessibilityPreferences')

      if (savedPreferences) {
        const preferences = JSON.parse(savedPreferences)

        // Apply saved preferences
        Object.keys(preferences).forEach((key) => {
          if (this[key] !== undefined) {
            // Special handling for fullscreen state
            if (key === 'isFullScreen') {
              // Just update the UI state but don't auto-enter fullscreen
              this.isFullScreen = false // Force to false regardless of saved state
            } else {
              this[key] = preferences[key]
            }
          }
        })

        // Apply loaded settings to the UI
        this.$nextTick(() => {
          if (this.isAutismMode) this.applyAutismFriendlyStyles()
          if (this.isReducedMotion) this.reduceAnimations()
          if (this.isPredictableLayout) this.applyPredictableLayout()
          if (this.isHighContrast) document.body.classList.add('high-contrast')
          if (this.isDyslexiaMode) document.body.classList.add('dyslexia-mode')
          if (this.isLargeText) document.body.classList.add('large-text')
          if (this.isSpacedText) document.body.classList.add('spaced-text')
          if (this.isLargeCursor) document.body.classList.add('large-cursor')
          if (this.isHighlightClickable) {
            this.highlightClickableElements()
            this.setupObserver()
          }
          // Removed the automatic fullscreen restoration

          document.body.classList.add(`align-${this.textAlignment}`)
          document.body.classList.add(`theme-${this.colorTheme}`)

          // Optionally: Show a notification that fullscreen mode was previously enabled
          if (preferences.isFullScreen) {
            this.showFullscreenRestoreNotification()
          }
        })
      }
    },

    showFullscreenRestoreNotification() {
      // Create a notification element
      const notification = document.createElement('div')
      notification.className = 'fullscreen-restore-notification'
      notification.innerHTML = `
        <div class="notification-content">
          <span>Vous étiez précédemment en mode plein écran.</span>
          <button class="restore-fullscreen-btn">Restaurer</button>
          <button class="close-notification-btn">×</button>
        </div>
      `

      // Style the notification
      notification.style.position = 'fixed'
      notification.style.top = '20px'
      notification.style.left = '50%'
      notification.style.transform = 'translateX(-50%)'
      notification.style.backgroundColor = 'rgba(30, 30, 45, 0.9)'
      notification.style.color = 'white'
      notification.style.padding = '12px 20px'
      notification.style.borderRadius = '8px'
      notification.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.3)'
      notification.style.zIndex = '9999'
      notification.style.display = 'flex'
      notification.style.alignItems = 'center'
      notification.style.backdropFilter = 'blur(5px)'
      notification.style.border = '1px solid rgba(255, 255, 255, 0.1)'
      notification.style.animation = 'fadeIn 0.3s ease'

      // Style the content
      const content = notification.querySelector('.notification-content')
      content.style.display = 'flex'
      content.style.alignItems = 'center'
      content.style.gap = '15px'

      // Style the buttons
      const restoreBtn = notification.querySelector('.restore-fullscreen-btn')
      restoreBtn.style.padding = '6px 12px'
      restoreBtn.style.backgroundColor = '#007bff'
      restoreBtn.style.border = 'none'
      restoreBtn.style.borderRadius = '4px'
      restoreBtn.style.color = 'white'
      restoreBtn.style.cursor = 'pointer'
      restoreBtn.style.transition = 'background-color 0.2s'

      const closeBtn = notification.querySelector('.close-notification-btn')
      closeBtn.style.padding = '6px 12px'
      closeBtn.style.backgroundColor = 'transparent'
      closeBtn.style.border = '1px solid rgba(255, 255, 255, 0.3)'
      closeBtn.style.borderRadius = '4px'
      closeBtn.style.color = 'white'
      closeBtn.style.cursor = 'pointer'
      closeBtn.style.transition = 'background-color 0.2s'
      closeBtn.style.fontSize = '16px'

      // Add animation styles
      const styleElement = document.createElement('style')
      styleElement.textContent = `
        @keyframes fadeIn {
          from { opacity: 0; transform: translate(-50%, -20px); }
          to { opacity: 1; transform: translate(-50%, 0); }
        }
      `
      document.head.appendChild(styleElement)

      // Add event listeners
      restoreBtn.addEventListener('click', () => {
        this.enterFullScreen()
        document.body.removeChild(notification)
      })

      closeBtn.addEventListener('click', () => {
        document.body.removeChild(notification)
      })

      // Auto-dismiss after 10 seconds
      setTimeout(() => {
        if (document.body.contains(notification)) {
          notification.style.animation = 'fadeIn 0.3s ease reverse'
          setTimeout(() => {
            if (document.body.contains(notification)) {
              document.body.removeChild(notification)
            }
          }, 300)
        }
      }, 10000)

      // Add to the DOM
      document.body.appendChild(notification)
    },

    resetAllSettings() {
      // Reset all settings to default
      this.isAutismMode = false
      this.isReducedMotion = false
      this.isPredictableLayout = false
      this.isHighContrast = false
      this.isDyslexiaMode = false
      this.isLargeText = false
      this.isSpacedText = false
      this.isLargeCursor = false
      this.isHighlightClickable = false
      this.showReadingMask = false
      this.showReadingGuide = false
      this.textAlignment = 'center'
      this.colorTheme = 'default'
      this.isFullScreen = false

      // Exit fullscreen if active
      if (document.fullscreenElement) {
        this.exitFullScreen()
      }

      // Remove all applied classes
      document.body.classList.remove(
        'autism-friendly',
        'reduced-motion',
        'predictable-layout',
        'high-contrast',
        'dyslexia-mode',
        'spaced-text',
        'large-cursor',
        'align-left',
        'align-center',
        'align-right',
      )
      document.documentElement.classList.remove('large-text')

      // Remove any applied themes
      document.body.className = document.body.className.replace(
        /theme-\w+/g,
        '',
      )

      // Remove reading guides and masks event listeners
      document.removeEventListener('mousemove', this.updateMaskPosition)
      document.removeEventListener('mousemove', this.updateGuidePosition)
      this.maskTopStyles = {}
      this.maskBottomStyles = {}
      this.maskLeftStyles = {}
      this.maskRightStyles = {}
      this.readingGuideStyles = {}

      // Disconnect observer if exists
      if (this.observer) {
        this.observer.disconnect()
        this.observer = null
      }

      // Remove clickable highlights
      const clickableElements = document.querySelectorAll(
        '.highlight-clickable',
      )
      clickableElements.forEach((element) => {
        element.classList.remove('highlight-clickable')
      })

      // Clear saved preferences
      localStorage.removeItem('accessibilityPreferences')
    },
  },
}
</script>

<style>
:root {
  --primary-color: #f97316;
  --primary-hover: #ea580c;
  --secondary-color: #64748b;
  --autism-font: 'Open Sans', 'Arial', sans-serif;
  --autism-line-height: 1.6;
  --autism-paragraph-spacing: 1.5em;
  --panel-bg: rgba(255, 255, 255, 0.95);
  --panel-text: #1e293b;
  --panel-border: #e2e8f0;
  --button-bg: #f8fafc;
  --button-hover: #f1f5f9;
  --active-option-bg: #fff7ed;
  --active-option-border: #fdba74;
  --transition-duration: 0.3s;
  --main-font: 'Glacial Indifference', sans-serif;

  /* Safe area insets pour iPhone */
  --safe-area-inset-top: env(safe-area-inset-top);
  --safe-area-inset-right: env(safe-area-inset-right);
  --safe-area-inset-bottom: env(safe-area-inset-bottom);
  --safe-area-inset-left: env(safe-area-inset-left);
}

/* Dark mode theme variables if needed */
.theme-dark {
  --panel-bg: rgba(30, 41, 59, 0.95);
  --panel-text: #f8fafc;
  --panel-border: #334155;
  --button-bg: #1e293b;
  --button-hover: #334155;
  --active-option-bg: #431407;
  --active-option-border: #9a3412;
}

/* Styles globaux pour iOS et Android */
html {
  width: 100%;
  height: 100%;
  overflow-x: hidden;
  -webkit-text-size-adjust: 100%;
  -webkit-tap-highlight-color: transparent;
  /* Pour Android Chrome */
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: optimizeLegibility;
}

body {
  width: 100%;
  min-height: 100vh;
  min-height: -webkit-fill-available;
  overflow-x: hidden;
  position: relative;
  margin: 0;
  padding: 0;
}

body {
  cursor: default;
  transition:
    background-color var(--transition-duration),
    color var(--transition-duration);
  font-family: var(--main-font);
}

h1,
h2,
h3,
h4,
h5,
h6,
p,
span,
div,
button,
a,
input,
textarea,
label {
  font-family: var(--main-font);
}

body {
  cursor: default;
  transition:
    background-color var(--transition-duration),
    color var(--transition-duration);
}

.app-container {
  min-height: 100vh;
  min-height: -webkit-fill-available;
  display: flex;
  flex-direction: column;
  position: relative;
  padding-top: var(--safe-area-inset-top);
  padding-right: var(--safe-area-inset-right);
  padding-bottom: var(--safe-area-inset-bottom);
  padding-left: var(--safe-area-inset-left);
}

.content-wrapper {
  flex: 1;
}

.dashboard-icon,
.mdi-fullscreen,
.mdi-fullscreen-exit {
  font-size: 28px;
  line-height: 1;
}

.fullscreen-button {
  position: fixed;
  top: 20px;
  right: 20px;
  background-color: #6366f1; /* Indigo */
  color: white;
  padding: 8px;
  width: 52px;
  height: 52px;
  border-radius: 50%;
  cursor: pointer;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
  transition: all 0.3s ease;
}

.fullscreen-button:hover {
  transform: scale(1.1);
  background-color: #4f46e5;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
}

.fullscreen-button.active {
  background-color: #4f46e5;
}

.fullscreen-button .tooltip {
  top: auto;
  bottom: -40px;
}

.dashboard-button {
  position: fixed;
  bottom: 20px;
  right: 85px;
  background-color: #10b981; /* Emerald */
  color: white;
  padding: 8px;
  width: 52px;
  height: 52px;
  border-radius: 50%;
  cursor: pointer;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
  transition: all 0.3s ease;
}

.dashboard-button:hover {
  transform: scale(1.1);
  background-color: #059669;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
}

/* Afficher le tooltip sur hover */
.dashboard-button:hover .tooltip {
  opacity: 1;
  visibility: visible;
}

/* ===== Scroll Navigation Buttons ===== */
.scroll-navigation-buttons {
  position: fixed;
  bottom: 85px;
  right: 20px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  z-index: 999;
}

.scroll-button {
  background-color: #3b82f6; /* Blue */
  color: white;
  padding: 6px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  cursor: pointer;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  position: relative;
}

.scroll-button:hover {
  transform: scale(1.1);
  background-color: #2563eb;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
}

.scroll-button i {
  font-size: 22px;
  line-height: 1;
}

.scroll-button:hover .tooltip {
  opacity: 1;
  visibility: visible;
}

/* Tooltip du bouton scroll-down en bas */
.scroll-button.scroll-down .tooltip {
  top: auto;
  bottom: -45px;
}

/* ===== Accessibility Widget ===== */
.accessibility-widget {
  position: fixed;
  bottom: 20px;
  right: 20px;
  background-color: var(--primary-color);
  color: white;
  padding: 8px;
  width: 52px;
  height: 52px;
  border-radius: 50%;
  cursor: pointer;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.accessibility-widget:hover {
  transform: scale(1.1) translateY(-2px);
  background-color: var(--primary-hover);
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
}

.accessibility-widget.active {
  background-color: var(--primary-hover);
}

.inclusive-icon {
  font-size: 28px;
}

.tooltip {
  position: absolute;
  top: -40px;
  left: 50%;
  transform: translateX(-50%);
  background-color: #1e293b;
  color: white;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
  opacity: 0;
  visibility: hidden;
  transition: all 0.2s ease;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.accessibility-widget:hover .tooltip {
  opacity: 1;
  visibility: visible;
  top: -45px;
}

/* ===== Accessibility Panel ===== */
.accessibility-panel {
  position: fixed;
  bottom: 85px;
  right: 20px;
  background-color: var(--panel-bg);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  border-radius: 16px;
  width: 380px;
  max-height: 80vh;
  overflow-y: auto;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  border: 1px solid var(--panel-border);
  animation: panelSlideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes panelSlideUp {
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid var(--panel-border);
}

.panel-header h2 {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: var(--panel-text);
  letter-spacing: -0.025em;
}

.close-button {
  background: var(--button-bg);
  border: 1px solid var(--panel-border);
  font-size: 20px;
  cursor: pointer;
  color: var(--panel-text);
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: all 0.2s;
}

.close-button:hover {
  background-color: var(--button-hover);
}

.panel-tabs {
  display: flex;
  padding: 8px;
  gap: 4px;
  background-color: rgba(0, 0, 0, 0.02);
  border-bottom: 1px solid var(--panel-border);
}

.panel-tabs button {
  flex: 1;
  background: none;
  border: none;
  padding: 8px 4px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  color: var(--secondary-color);
  border-radius: 8px;
  transition: all 0.2s;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}

.panel-tabs button i {
  font-size: 18px;
}

.panel-tabs button:hover {
  background-color: var(--button-hover);
  color: var(--panel-text);
}

.panel-tabs button.active-tab {
  background-color: white;
  color: var(--primary-color);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.panel-content {
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.option-item {
  width: 100%;
}

.option-item button {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background-color: var(--button-bg);
  border: 1px solid var(--panel-border);
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 14px;
  font-weight: 500;
  color: var(--panel-text);
  text-align: left;
}

.option-item button:hover {
  background-color: var(--button-hover);
  border-color: var(--secondary-color);
  transform: translateX(4px);
}

.option-item button.active-option {
  background-color: var(--active-option-bg);
  border-color: var(--primary-color);
  color: var(--primary-color);
}

.option-icon {
  font-size: 20px;
}

.option-text {
  flex: 1;
}

.option-description {
  font-size: 12px;
  color: var(--secondary-color);
  margin: 4px 0 0 48px;
  display: block;
}

.button-group {
  display: flex;
  gap: 8px;
  margin-top: 4px;
}

.small-button {
  padding: 10px;
  font-size: 18px;
  flex: 1;
  justify-content: center !important;
  transform: none !important;
}

.option-label {
  font-size: 13px;
  font-weight: 600;
  color: var(--secondary-color);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 8px;
  display: block;
}

.panel-footer {
  padding: 16px 20px;
  border-top: 1px solid var(--panel-border);
  display: flex;
  justify-content: center;
}

.reset-button {
  background-color: #fee2e2;
  color: #dc2626;
  border: 1px solid #fecaca;
  padding: 10px 20px;
  border-radius: 10px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  justify-content: center;
}

.reset-button:hover {
  background-color: #fef2f2;
  transform: scale(1.02);
}

.reset-button i {
  font-size: 18px;
}

/* ===== Quit Zone for Sensory Overload ===== */
.quit-zone {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: #f5f5f5;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.quit-zone-content {
  max-width: 500px;
  text-align: center;
  padding: 40px;
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.quit-zone-content h2 {
  margin-top: 0;
  color: #333;
}

.breathing-guide {
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background-color: #c9e6ff;
  margin: 24px auto;
  animation: breathing 6s infinite ease-in-out;
}

@keyframes breathing {
  0% {
    transform: scale(0.9);
    background-color: #c9e6ff;
  }
  25% {
    transform: scale(1);
    background-color: #94c9ff;
  }
  60% {
    transform: scale(0.9);
    background-color: #c9e6ff;
  }
  100% {
    transform: scale(0.9);
    background-color: #c9e6ff;
  }
}

/* ===== Various Mode Styles ===== */
/* Autism-friendly mode */
.autism-mode {
  font-family: var(--autism-font);
}

.autism-mode p,
.autism-mode li,
.autism-mode span,
.autism-mode div {
  line-height: var(--autism-line-height);
}

.autism-mode p {
  margin-bottom: var(--autism-paragraph-spacing);
}

.autism-mode a {
  text-decoration: underline;
  font-weight: 500;
}

/* Reduced motion */
.reduced-motion * {
  animation-duration: 0.001ms !important;
  transition-duration: 0.001ms !important;
}

/* Predictable layout */
.predictable-layout * {
  border-radius: 8px;
}

.predictable-layout nav,
.predictable-layout header,
.predictable-layout footer {
  position: static !important;
}

/* High contrast mode */
.high-contrast {
  color: black !important;
  background-color: white !important;
}

.high-contrast * {
  color: black !important;
  background-color: white !important;
  border-color: black !important;
}

.high-contrast a,
.high-contrast button {
  color: #0000ee !important;
  border: 2px solid #0000ee !important;
}

.high-contrast img {
  filter: grayscale(100%) contrast(200%);
}

/* Dyslexia mode */
.dyslexia-mode {
  font-family: 'OpenDyslexic', 'Comic Sans MS', cursive;
  font-size: 1.05em;
  letter-spacing: 0.05em;
  word-spacing: 0.1em;
}

.dyslexia-mode p {
  line-height: 1.7;
}

/* Large text - Applied to html tag */
html.large-text {
  font-size: 130% !important; /* Increases base rem size */
}

/* Fallback for fixed pixels if needed, or forcing specific elements */
.large-text h1, .large-text h2, .large-text h3, .large-text p, .large-text span, .large-text button, .large-text a, .large-text li {
  font-size: 1.2em; /* Additional relative boost if rem isn't used everywhere */
}

/* Spaced text - Applied to body, forcing children */
.spaced-text * {
  letter-spacing: 0.15em !important;
  word-spacing: 0.2em !important;
  line-height: 2 !important;
}

.spaced-text p,
.spaced-text li,
.spaced-text span,
.spaced-text div {
  margin-bottom: 1.5em; /* Add breathing room between blocks */
}

/* Large cursor */
.large-cursor {
  cursor:
    url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48" fill="none" stroke="black" stroke-width="4"><path d="M12,12 L36,36 M12,36 L36,12"/></svg>')
      24 24,
    auto;
}

/* Text alignment */
.align-left {
  text-align: left;
}

.align-center {
  text-align: center;
}

.align-right {
  text-align: right;
}

/* Highlight clickable elements */
.highlight-clickable {
  outline: 3px solid #ff9800 !important;
  outline-offset: 2px !important;
  box-shadow: 0 0 10px rgba(255, 152, 0, 0.5) !important;
}

/* Reading mask and guide */
.reading-mask-overlay {
  position: fixed;
  background-color: rgba(0, 0, 0, 0.6);
  z-index: 998;
  pointer-events: none;
  transition: all 0.1s ease;
}

.mask-top {
  top: 0;
  left: 0;
  width: 100%;
}

.mask-bottom {
  bottom: 0;
  left: 0;
  width: 100%;
}

.mask-left {
  left: 0;
  width: 0; /* Slit only by default, can be adjusted */
}

.mask-right {
  right: 0;
  width: 0;
}

.reading-guide {
  position: fixed;
  height: 4px;
  background-color: var(--primary-color);
  box-shadow: 0 0 10px var(--primary-color);
  z-index: 997;
  pointer-events: none;
  transition: all 0.1s ease;
  border-radius: 2px;
}

/* Color Themes */
.theme-warm {
  background-color: #fffaf0;
  color: #5d4037;
}

.theme-warm a {
  color: #d84315;
}

.theme-warm button:not(.active-option) {
  background-color: #ffccbc;
}

.theme-cool {
  background-color: #f1f8ff;
  color: #37474f;
}

.theme-cool a {
  color: #0288d1;
}

.theme-cool button:not(.active-option) {
  background-color: #bbdefb;
}

.theme-dark {
  background-color: #121212;
  color: #e0e0e0;
}

.theme-dark a {
  color: #90caf9;
}

.theme-dark button:not(.active-option) {
  background-color: #2c2c2c;
  color: #e0e0e0;
}

.theme-dark .accessibility-panel {
  background-color: #1e1e1e;
  border-color: #333;
}

.theme-dark .panel-header,
.theme-dark .panel-footer {
  border-color: #333;
}

.theme-dark .panel-header h2 {
  color: #e0e0e0;
}

.theme-dark .panel-tabs {
  background-color: #2c2c2c;
}

.theme-monochrome {
  filter: grayscale(100%);
}

/* Responsive design for mobile */

/* Très grands écrans (> 1920px) */
@media (min-width: 1921px) {
  .fullscreen-button,
  .dashboard-button,
  .accessibility-widget {
    width: 60px;
    height: 60px;
    padding: 10px;
  }

  .fullscreen-icon,
  .home-icon {
    font-size: 28px;
  }

  .inclusive-icon {
    width: 44px;
    height: 44px;
  }
}

/* Écrans moyens (1024px - 1440px) */
@media (max-width: 1440px) {
  .fullscreen-button,
  .dashboard-button,
  .accessibility-widget {
    width: 48px;
    height: 48px;
  }

  .fullscreen-icon,
  .home-icon {
    font-size: 22px;
  }

  .inclusive-icon {
    width: 32px;
    height: 32px;
  }
}

/* Tablettes (768px - 1024px) */
@media (max-width: 1024px) {
  .fullscreen-button {
    width: 44px;
    height: 44px;
    top: 15px;
    right: 15px;
  }

  .dashboard-button {
    width: 44px;
    height: 44px;
    bottom: 15px;
    right: 70px;
  }

  .accessibility-widget {
    width: 44px;
    height: 44px;
    bottom: 15px;
    right: 15px;
  }

  .scroll-navigation-buttons {
    bottom: 70px;
    right: 15px;
    gap: 6px;
  }

  .scroll-button {
    width: 44px;
    height: 44px;
  }

  .scroll-button i {
    font-size: 24px;
  }

  .fullscreen-icon,
  .home-icon {
    font-size: 20px;
  }

  .inclusive-icon {
    width: 30px;
    height: 30px;
  }

  .accessibility-panel {
    width: 85%;
    max-width: 350px;
    max-height: 65vh;
  }
}

/* Mobiles en mode paysage et petites tablettes (576px - 768px) */
@media (max-width: 768px) {
  .fullscreen-button {
    width: 40px;
    height: 40px;
    top: 10px;
    right: 10px;
  }

  .dashboard-button {
    width: 40px;
    height: 40px;
    bottom: 10px;
    right: 60px;
  }

  .accessibility-widget {
    width: 40px;
    height: 40px;
    bottom: 10px;
    right: 10px;
  }

  .scroll-navigation-buttons {
    bottom: 60px;
    right: 10px;
    gap: 6px;
  }

  .scroll-button {
    width: 40px;
    height: 40px;
  }

  .scroll-button i {
    font-size: 22px;
  }

  .fullscreen-icon,
  .home-icon {
    font-size: 18px;
  }

  .inclusive-icon {
    width: 28px;
    height: 28px;
  }

  .accessibility-panel {
    width: 90%;
    max-width: 320px;
    max-height: 60vh;
    right: 5%;
    bottom: 70px;
  }

  .panel-content {
    padding: 10px;
  }

  .option-item button {
    padding: 8px 10px;
    font-size: 0.9rem;
  }

  .tooltip {
    font-size: 11px;
    padding: 4px 8px;
  }
}

/* Petits mobiles (< 576px) */
@media (max-width: 576px) {
  .fullscreen-button {
    width: 36px;
    height: 36px;
    top: 8px;
    right: 8px;
  }

  .dashboard-button {
    width: 36px;
    height: 36px;
    bottom: 8px;
    right: 52px;
  }

  .accessibility-widget {
    width: 36px;
    height: 36px;
    bottom: 8px;
    right: 8px;
  }

  .scroll-navigation-buttons {
    bottom: 52px;
    right: 8px;
    gap: 5px;
  }

  .scroll-button {
    width: 36px;
    height: 36px;
  }

  .scroll-button i {
    font-size: 20px;
  }

  .fullscreen-icon,
  .home-icon {
    font-size: 16px;
  }

  .inclusive-icon {
    width: 24px;
    height: 24px;
  }

  .accessibility-panel {
    width: 95%;
    max-width: 300px;
    max-height: 55vh;
    right: 2.5%;
    bottom: 60px;
    font-size: 0.85rem;
  }

  .panel-content {
    padding: 8px;
  }

  .option-item button {
    padding: 6px 8px;
    font-size: 0.85rem;
  }

  .option-item label {
    font-size: 0.85rem;
  }

  .tooltip {
    font-size: 10px;
    padding: 3px 6px;
  }
}

/* Très petits écrans (< 375px) */
@media (max-width: 375px) {
  .fullscreen-button {
    width: 32px;
    height: 32px;
    top: 5px;
    right: 5px;
  }

  .dashboard-button {
    width: 32px;
    height: 32px;
    bottom: 5px;
    right: 45px;
  }

  .accessibility-widget {
    width: 32px;
    height: 32px;
    bottom: 5px;
    right: 5px;
  }

  .scroll-navigation-buttons {
    bottom: 45px;
    right: 5px;
    gap: 4px;
  }

  .scroll-button {
    width: 32px;
    height: 32px;
  }

  .scroll-button i {
    font-size: 18px;
  }

  .fullscreen-icon,
  .home-icon {
    font-size: 14px;
  }

  .inclusive-icon {
    width: 20px;
    height: 20px;
  }

  .accessibility-panel {
    width: 98%;
    max-width: 280px;
    max-height: 50vh;
    right: 1%;
    bottom: 50px;
    font-size: 0.8rem;
  }

  .panel-content {
    padding: 6px;
  }

  .option-item button {
    padding: 5px 6px;
    font-size: 0.8rem;
  }

  .option-item label {
    font-size: 0.8rem;
  }

  .tooltip {
    display: none; /* Masquer les tooltips sur très petits écrans */
  }
}
</style>